/**
 * Homepage · Conversion V1
 *
 * Public marketing homepage. Optimises for consultant free-trial signups.
 * Structure (fixed):
 *   1. Hero            — outcome-led headline, dual CTA, trust chips
 *   2. Social Proof    — logo strip + a marquee stat row
 *   3. How It Works    — 4 numbered steps
 *   4. Why Consultants Choose BizPulseAI — 6 differentiator cards
 *   5. Everything Included — feature grid
 *   6. Pricing CTA     — reuses <PricingSection>
 *   7. FAQ             — Shadcn accordion, 8 rows
 *   8. Final CTA       — one-liner headline + primary CTA
 *
 * Reuses existing brand tokens (navy background + accent), Shadcn Card,
 * Button, Accordion primitives, lucide-react icons, and the existing
 * PricingSection + ContactSection components.
 */
import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import PricingSection from "../components/PricingSection";
import ContactSection from "../components/ContactSection";
import { useBrand } from "../lib/brand";
import { useAuth } from "../lib/auth";
import { useTranslation } from "react-i18next";
import {
  ArrowRight, PlayCircle, ShieldCheck, Check, Sparkles, Timer,
  BadgeCheck, Users, MessageSquare, FileText, Globe2, Zap, Link as LinkIcon,
  ScrollText, Layers, TrendingUp, Star,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxleGVjdXRpdmUlMjBidXNpbmVzcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzgyMDQ4Mzk4fDA&ixlib=rb-4.1.0&q=85";

// --------------------------------------------------------------------------
// Static content
// --------------------------------------------------------------------------
// STATS / FAQ / DIFFERENTIATORS bodies now live in `/src/i18n/locales/*.json`.
// Only the icon-bearing scaffolds remain here — text is pulled via `t()`.
// --------------------------------------------------------------------------

const HOW_IT_WORKS = [
  { step: "01", stageKey: "attract",  icon: Layers },
  { step: "02", stageKey: "diagnose", icon: LinkIcon },
  { step: "03", stageKey: "convert",  icon: Timer },
  { step: "04", stageKey: "nurture",  icon: MessageSquare },
  { step: "05", stageKey: "grow",     icon: TrendingUp },
];

// Six icons only — text is served by i18n at `landing.whyChoose.cards`.
const DIFFERENTIATORS = [
  { icon: BadgeCheck },
  { icon: Sparkles },
  { icon: TrendingUp },
  { icon: MessageSquare },
  { icon: Globe2 },
  { icon: ShieldCheck },
];

// Reorganised under the five stages of the BizPulseAI Consulting
// Growth Engine™ so visitors feel they are replacing multiple tools
// with one platform. Functionality is unchanged — this is purely a
// copy + grouping change.
const FEATURE_GROUPS = [
  {
    groupKey: "attract",
    items: [
      { icon: LinkIcon, key: "branded-pages" },
      { icon: Star, key: "qr-campaigns" },
      { icon: Users, key: "lead-capture" },
    ],
  },
  {
    groupKey: "diagnose",
    items: [
      { icon: Sparkles, key: "ai-assessments" },
      { icon: FileText, key: "growth-reports" },
      { icon: BadgeCheck, key: "grounded-citations" },
      { icon: Globe2, key: "peer-benchmarks" },
      { icon: ScrollText, key: "regulatory-panel" },
    ],
  },
  {
    groupKey: "convert",
    items: [
      { icon: Layers, key: "proposal-builder" },
      { icon: MessageSquare, key: "discovery-booking" },
    ],
  },
  {
    groupKey: "nurture",
    items: [
      { icon: TrendingUp, key: "email-drips" },
      { icon: Star, key: "linkedin-assets" },
    ],
  },
  {
    groupKey: "grow",
    items: [
      { icon: TrendingUp, key: "analytics" },
      { icon: ShieldCheck, key: "tenant-isolated" },
    ],
  },
];

// FAQ items now live in `/src/i18n/locales/*.json` under `landing.faq.items`.

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------
export default function Landing() {
  const nav = useNavigate();
  const loc = useLocation();
  const { tenant } = useBrand();
  const { user } = useAuth();
  const { t } = useTranslation();

  const heroTitle = tenant?.brand_headline || t("landing.hero.title");
  const heroSub = tenant?.brand_subheadline || t("landing.hero.subheadline");

  useEffect(() => {
    const target = loc.state?.scrollTo;
    if (!target) return;
    const t = setTimeout(() => {
      const el = document.getElementById(target);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        nav("/", { replace: true, state: {} });
      }
    }, 80);
    return () => clearTimeout(t);
  }, [loc.state, nav]);

  const primaryCtaLabel = user ? t("landing.hero.primaryCtaAuthed") : t("landing.hero.primaryCta");
  const primaryCtaTarget = user ? "/dashboard" : "/register";

  return (
    <div className="overflow-hidden" data-testid="landing-page">
      {/* ============================= 1 · Hero ============================= */}
      <section className="relative" data-testid="hero-section">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-20" />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, hsla(222,40%,8%,0.88) 0%, hsla(222,40%,8%,0.94) 60%, hsl(222,40%,8%) 100%)",
            }}
          />
        </div>
        <div className="ambient-glow w-[600px] h-[600px] -top-40 -left-40" style={{ background: "hsl(34, 30%, 75%)" }} />
        <div className="ambient-glow w-[500px] h-[500px] top-20 -right-40" style={{ background: "hsl(217, 91%, 60%)" }} />

        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-32 lg:pb-32 relative">
          <div className="max-w-4xl">
            <div className="overline text-accent fade-up-1" data-testid="hero-eyebrow">
              <Sparkles className="w-3.5 h-3.5 inline mr-1" /> {t("landing.hero.eyebrow")}
            </div>
            <h1
              className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.05] tracking-tight fade-up-2"
              data-testid="hero-headline"
            >
              {heroTitle}
            </h1>
            <p
              className="text-lg sm:text-xl text-muted-foreground mt-6 max-w-2xl leading-relaxed fade-up-3"
              data-testid="hero-subheadline"
            >
              {heroSub}
            </p>
            <p
              className="text-base text-foreground/90 mt-4 max-w-2xl leading-relaxed fade-up-3 font-sans-ui"
              data-testid="hero-outcome-line"
            >
              {t("landing.hero.outcomeLine")}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3 fade-up-3">
              <Button
                size="lg"
                className="rounded-full px-8 h-12 text-base"
                onClick={() => nav(primaryCtaTarget)}
                data-testid="hero-primary-cta"
              >
                {primaryCtaLabel} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8 h-12 text-base"
                onClick={() => nav("/sample")}
                data-testid="hero-secondary-cta"
              >
                <PlayCircle className="w-4 h-4 mr-2" /> {t("landing.hero.secondaryCta")}
              </Button>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground fade-up-3">
              <span className="flex items-center gap-2" data-testid="hero-chip-refund">
                <Check className="w-3.5 h-3.5 text-accent" /> {t("landing.hero.chips.attract")}
              </span>
              <span className="flex items-center gap-2" data-testid="hero-chip-cancel">
                <Check className="w-3.5 h-3.5 text-accent" /> {t("landing.hero.chips.win")}
              </span>
              <span className="flex items-center gap-2" data-testid="hero-chip-tenant">
                <Check className="w-3.5 h-3.5 text-accent" /> {t("landing.hero.chips.leadsYours")}
              </span>
              <span className="flex items-center gap-2" data-testid="hero-chip-cited">
                <Check className="w-3.5 h-3.5 text-accent" /> {t("landing.hero.chips.moneyBack")}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ========================== 2 · Social proof ========================== */}
      <section className="border-y border-border/40 bg-secondary/20" data-testid="social-proof-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
          <p className="text-xs uppercase tracking-widest text-muted-foreground text-center">
            {t("landing.social.intro")}
          </p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            {(t("landing.social.stats", { returnObjects: true }) || []).map((s, i) => (
              <div key={i} className="text-center" data-testid={`stat-${i}`}>
                <div className="font-display text-3xl lg:text-4xl text-accent">{s.value}</div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================== 3 · How It Works =========================== */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28" data-testid="how-it-works-section">
        <div className="max-w-3xl">
          <div className="overline text-accent">{t("landing.howItWorks.eyebrow")}</div>
          <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">
            {t("landing.howItWorks.headlineLead")}<span className="italic text-accent">{t("landing.howItWorks.headlineAccent")}</span>
          </h2>
          <p className="text-muted-foreground mt-4 text-lg leading-relaxed">
            {t("landing.howItWorks.sub")}
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-5 gap-5">
          {HOW_IT_WORKS.map((s, i) => (
            <Card
              key={s.step}
              className="card-elevated p-6 relative flex flex-col"
              data-testid={`how-step-${i + 1}`}
            >
              <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-accent text-accent-foreground font-mono text-sm flex items-center justify-center shadow-lg">
                {s.step}
              </div>
              <s.icon className="w-6 h-6 text-accent mt-4" />
              <div className="text-[10px] uppercase tracking-widest text-accent/80 font-sans-ui mt-4">
                {t(`landing.howItWorks.steps.${s.stageKey}.stage`)}
              </div>
              <div className="font-sans-ui font-semibold mt-1 text-lg">
                {t(`landing.howItWorks.steps.${s.stageKey}.title`)}
              </div>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed flex-1">
                {t(`landing.howItWorks.steps.${s.stageKey}.body`)}
              </p>
            </Card>
          ))}
        </div>
      </section>

      {/* ================= 4 · Why Consultants Choose BizPulseAI ================= */}
      <section
        className="bg-secondary/30 border-y border-border/40 py-20 lg:py-28"
        data-testid="why-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="overline text-accent">{t("landing.whyChoose.eyebrow")}</div>
            <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">
              {t("landing.whyChoose.headlineLead")}<span className="italic text-accent">{t("landing.whyChoose.headlineAccent")}</span>
            </h2>
            <p className="text-muted-foreground mt-4 text-lg leading-relaxed">
              {t("landing.whyChoose.sub")}
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {DIFFERENTIATORS.map((d, i) => {
              const card = (t("landing.whyChoose.cards", { returnObjects: true }) || [])[i] || {};
              const title = card.title || "";
              return (
                <Card
                  key={i}
                  className="card-elevated p-6 flex flex-col"
                  data-testid={`why-card-${i}`}
                >
                  <d.icon className="w-6 h-6 text-accent" />
                  <div className="font-sans-ui font-semibold mt-4 text-lg">{title}</div>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed flex-1">{card.body}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ======================== 5 · Everything Included ======================== */}
      <section
        className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28"
        data-testid="everything-included-section"
      >
        <div className="max-w-3xl">
          <div className="overline text-accent">{t("landing.included.eyebrow")}</div>
          <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">
            {t("landing.included.headlineLead")}<span className="italic text-accent">{t("landing.included.headlineAccent")}</span>
          </h2>
          <p className="text-muted-foreground mt-4 text-lg leading-relaxed">
            {t("landing.included.sub")}
          </p>
        </div>

        <div className="mt-12 space-y-8">
          {FEATURE_GROUPS.map((g) => (
            <div key={g.groupKey} data-testid={`feature-group-${g.groupKey}`}>
              <div className="text-xs uppercase tracking-widest text-accent font-sans-ui mb-3">
                {t(`landing.included.groups.${g.groupKey}.label`)}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {g.items.map((f) => (
                  <div
                    key={f.key}
                    className="flex items-center gap-3 rounded-xl border border-border/60 bg-secondary/30 px-4 py-3"
                    data-testid={`feature-${f.key}`}
                  >
                    <f.icon className="w-4 h-4 text-accent shrink-0" />
                    <span className="text-sm">
                      {t(`landing.included.groups.${g.groupKey}.items.${f.key}`)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============================ 6 · Pricing CTA ============================ */}
      <div id="pricing" data-testid="pricing-section-anchor">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center pt-24 lg:pt-28" data-testid="pricing-roi-intro">
          <p className="font-display text-2xl sm:text-3xl leading-snug text-foreground">
            {t("landing.pricingIntro.headlineLead")}<span className="italic text-accent">{t("landing.pricingIntro.headlineAccent")}</span>{t("landing.pricingIntro.headlineTail")}
          </p>
          <p className="text-muted-foreground mt-4 text-base leading-relaxed">
            {t("landing.pricingIntro.sub")}
          </p>
        </div>
        <PricingSection
          eyebrow={t("landing.pricing.eyebrow")}
          heading={
            <>
              {t("landing.pricing.headlineLead")}<span className="italic text-accent">{t("landing.pricing.headlineAccent")}</span>
            </>
          }
          subheading={t("landing.pricing.sub")}
        />
      </div>

      {/* ================================ 7 · FAQ ================================ */}
      <section
        className="max-w-4xl mx-auto px-6 lg:px-10 py-20 lg:py-28"
        data-testid="faq-section"
        id="faq"
      >
        <div className="text-center">
          <div className="overline text-accent">{t("landing.faq.eyebrow")}</div>
          <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">
            {t("landing.faq.headline")}
          </h2>
        </div>

        <Accordion type="single" collapsible className="mt-10">
          {(t("landing.faq.items", { returnObjects: true }) || []).map((item, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              data-testid={`faq-item-${i + 1}`}
            >
              <AccordionTrigger className="text-left text-base font-medium">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* ============================ 8 · Final CTA ============================ */}
      <section
        className="relative overflow-hidden border-y border-border/40 bg-secondary/30"
        data-testid="final-cta-section"
      >
        <div className="ambient-glow w-[500px] h-[500px] -top-40 -right-40" style={{ background: "hsl(34, 30%, 75%)" }} />
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 lg:py-28 text-center relative">
          <div className="overline text-accent">{t("landing.finalCta.eyebrow")}</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">
            {t("landing.finalCta.headlineLead")}<span className="italic text-accent">{t("landing.finalCta.headlineAccent")}</span>
          </h2>
          <p className="text-muted-foreground mt-6 text-lg max-w-2xl mx-auto leading-relaxed">
            {t("landing.finalCta.sub")}
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Button
              size="lg"
              className="rounded-full px-8 h-12 text-base"
              onClick={() => nav(primaryCtaTarget)}
              data-testid="final-primary-cta"
            >
              {primaryCtaLabel} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="rounded-full px-8 h-12 text-base"
              onClick={() => nav("/sample")}
              data-testid="final-secondary-cta"
            >
              <PlayCircle className="w-4 h-4 mr-2" /> {t("landing.finalCta.secondaryCta")}
            </Button>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground uppercase tracking-widest" data-testid="final-cta-reassurance">
            {(t("landing.finalCta.reassurance", { returnObjects: true }) || []).map((r, i) => (
              <span key={i} className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-accent" /> {r}</span>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground uppercase tracking-widest">
            {(() => {
              const trust = t("landing.finalCta.trust", { returnObjects: true }) || [];
              const icons = [ShieldCheck, Zap, BadgeCheck];
              return trust.map((v, i) => {
                const Icon = icons[i] || Check;
                return (
                  <span key={i} className="flex items-center gap-2"><Icon className="w-3.5 h-3.5" /> {v}</span>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {/* Contact block preserved from previous homepage for enterprise leads */}
      <ContactSection />
    </div>
  );
}
